import { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Input, Stack, Switch, Typography } from '@mui/joy';
import { LayoutContext } from '../../context';
import { APIGetDoctorCalendar, APISaveDoctorCalendar } from '../../api';
import { getAccessToken } from '../../auth';

export default function DoctorCalendarField({ profileId, isDoctor }) {
  const { account, currentDoctor, token } = useContext(LayoutContext);
  const actor = account || currentDoctor;
  const allowed = actor?.type_profile?.some((r) => ['admin', 'moderator', 'supervisor'].includes(r))
    || (Number(actor?.id) === Number(profileId) && actor?.type_profile?.some((r) => ['doctor', 'duty_doctor'].includes(r)));
  const [url, setUrl] = useState('');
  const [zone, setZone] = useState('Europe/Moscow');
  const [enabled, setEnabled] = useState(true);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const epoch = useRef(0);
  const saving = useRef(false);
  useEffect(() => {
    const generation = ++epoch.current;
    setUrl(''); setZone('Europe/Moscow'); setEnabled(true); setState(null);
    setError(''); setMessage(''); setBusy(false); saving.current = false;
    setLoading(Boolean(allowed && isDoctor && profileId));
    if (!(allowed && isDoctor && profileId)) return;
    APIGetDoctorCalendar(profileId, getAccessToken() || token).then((data) => {
      if (epoch.current !== generation) return;
      setUrl(data.feed_url || ''); setZone(data.timezone); setEnabled(data.enabled); setState(data);
    }).catch(() => {
      if (epoch.current === generation) setError('Не удалось загрузить календарь. Откройте карточку повторно.');
    }).finally(() => {
      if (epoch.current === generation) setLoading(false);
    });
    return () => { epoch.current++; };
  }, [profileId, isDoctor, allowed, actor?.id, token]);
  if (!allowed || !isDoctor) return null;
  if (!profileId) return <Alert color="neutral">Сначала создайте врача, затем откройте его карточку и добавьте календарь.</Alert>;
  const save = async () => {
    if (saving.current || loading || !state) return;
    const generation = epoch.current;
    const targetId = profileId;
    saving.current = true; setBusy(true); setError(''); setMessage('');
    try {
      const data = await APISaveDoctorCalendar(targetId, getAccessToken() || token,
        { feed_url: url.trim(), timezone: zone.trim(), enabled });
      if (epoch.current !== generation) return;
      setState(data); setUrl(data.feed_url || ''); setEnabled(data.enabled);
      setMessage(data.message);
    } catch (e) {
      if (epoch.current === generation) setError(e?.response?.data?.error || 'Не удалось сохранить календарь');
    } finally {
      if (epoch.current === generation) { saving.current = false; setBusy(false); }
    }
  };
  return <Box sx={{ p: 2, borderRadius: 'lg', bgcolor: 'background.level1' }}>
    <Stack spacing={1.5}>
      <Typography level="title-lg">Календарь врача</Typography>
      <Typography level="body-sm">Отдельный календарь для этого врача. Меняйте смены в Яндексе — сайт проверяет график каждый час.</Typography>
      {error && <Alert color="danger">{error}</Alert>}
      {message && <Alert color="success">{message}</Alert>}
      <FormControl><FormLabel>Ссылка на календарь врача (iCal)</FormLabel>
        <Input type="password" autoComplete="new-password" disabled={loading || busy || !state}
          value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Вставьте ссылку экспорта iCal из Яндекса" />
      </FormControl>
      <Typography level="body-xs">Яндекс → настройки именно этого календаря → Экспорт → iCal. Чтобы удалить привязку, очистите поле и сохраните.</Typography>
      <FormControl><FormLabel>Часовой пояс событий без указанного времени/пояса</FormLabel>
        <Input disabled={loading || busy || !state} value={zone} onChange={(e) => setZone(e.target.value)}
          placeholder="Europe/Moscow или Asia/Yekaterinburg" />
      </FormControl>
      <Typography level="body-xs">События «Весь день» считаются сменой с 00:00 до 00:00 следующего дня в этом поясе. В чате время отображается по Москве.</Typography>
      <FormControl orientation="horizontal"><FormLabel>Показывать расписание</FormLabel>
        <Switch disabled={loading || busy || !state} checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
      </FormControl>
      <Typography level="body-xs">{loading ? 'Загружаем…' : state?.last_error ? 'Ошибка обновления: проверьте ссылку; повторим автоматически при следующем запуске.'
        : state?.synced_at ? `Последнее обновление: ${new Date(state.synced_at).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })} (Москва)` : 'После подключения ожидаем ближайшее автоматическое обновление.'}</Typography>
      <Button loading={busy} disabled={loading || !state} onClick={save}>Сохранить календарь</Button>
    </Stack>
  </Box>;
}